import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Palette, Save } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";

const PALETTE = [
  "#2563EB","#3B82F6","#0EA5E9","#06B6D4","#10B981","#059669",
  "#84CC16","#F59E0B","#F97316","#EF4444","#DC2626","#EC4899",
  "#7C3AED","#A855F7","#64748B","#475569","#0F172A","#FFFFFF",
];

export default function StatusColors() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(null);

  async function load() {
    const { data } = await api.get("/status-colors");
    setItems(data);
  }
  useEffect(() => {
    load();
  }, []);

  async function save(item) {
    setSaving(item.key);
    try {
      await api.put(`/status-colors/${item.key}`, {
        key: item.key,
        label: item.label,
        color: item.color,
        description: item.description,
      });
      toast.success(t('Cor de "{label}" salva', { label: item.label }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  }

  function update(key, patch) {
    setItems((arr) => arr.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
          {t("Personalização")}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Palette size={26} /> {t("Cores das atividades")}
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          {t("Personalize as cores que aparecem na agenda, dashboard e cards conforme o status da limpeza")}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div
            className="cm-card p-5"
            key={it.key}
            data-testid={`status-card-${it.key}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-12 h-12 rounded-xl border border-slate-200"
                style={{ background: it.color }}
              />
              <div className="flex-1 min-w-0">
                <input
                  data-testid={`status-label-${it.key}`}
                  className="cm-input font-semibold text-slate-900"
                  value={it.label || ""}
                  onChange={(e) => update(it.key, { label: e.target.value })}
                />
                <div className="text-[11px] text-slate-500 mt-1 font-mono">
                  {it.key}
                </div>
              </div>
            </div>

            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              {t("Escolha a cor")}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`status-${it.key}-color-${c.replace("#", "")}`}
                  onClick={() => update(it.key, { color: c })}
                  className={`w-8 h-8 rounded-full transition-transform border ${
                    (it.color || "").toUpperCase() === c
                      ? "ring-2 ring-offset-2 ring-slate-900 scale-110 border-transparent"
                      : "border-slate-200 hover:scale-105"
                  }`}
                  style={{ background: c }}
                />
              ))}
              <input
                type="color"
                aria-label={t("Cor personalizada")}
                value={it.color || "#000000"}
                onChange={(e) => update(it.key, { color: e.target.value })}
                className="w-8 h-8 border border-slate-200 rounded cursor-pointer"
              />
            </div>

            <input
              data-testid={`status-desc-${it.key}`}
              className="cm-input text-sm"
              placeholder={t("Descrição (opcional)")}
              value={it.description || ""}
              onChange={(e) => update(it.key, { description: e.target.value })}
            />

            <div className="flex items-center justify-between mt-3">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: hexToRgba(it.color, 0.12),
                  color: it.color,
                  border: `1px solid ${hexToRgba(it.color, 0.3)}`,
                }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: it.color }}
                />
                Preview · {it.label}
              </span>
              <Button
                data-testid={`status-save-${it.key}`}
                size="sm"
                onClick={() => save(it)}
                disabled={saving === it.key}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save size={13} className="mr-1" />
                {saving === it.key ? t("Salvando...") : t("Salvar")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
